/**
 * A regra das tabelas que se ordenam por clique no cabeçalho.
 *
 * Papel: aqui ficam como cada tipo de valor se compara, como um clique troca a
 * coluna e a direção, e a ordenação das linhas, nas duas regras que as tabelas
 * da origem seguem: a `'alternada'`, das três tabelas de Manutenções (l.
 * 868–912), e a `'com-retorno'`, das tabelas de Documentos, Saúde e RH.
 *
 * Como usar: cada tabela declara, por coluna, o `tipo` e a função que tira o
 * valor da linha, numa constante fora do componente, e passa essa declaração ao
 * `useOrdenacao`. A declaração precisa ficar fora do componente porque o hook a
 * usa como dependência de memória: criada a cada render, ela reordena a tabela a
 * cada render. Uma coluna declarada com o tipo errado continua ordenando, mas
 * pela regra do outro tipo — um custo declarado como `texto` põe "1000" antes de
 * "900", e nada acusa o engano.
 *
 * Na regra `'alternada'`, o vazio vem antes de tudo na crescente, em qualquer
 * tipo, porque é o que a origem de Manutenções faz: `-Infinity` para número e
 * texto vazio para o resto. `NaN` conta como vazio, e é isso que impede um "#"
 * digitado como "3A" de deixar a ordem inconsistente.
 *
 * Na regra `'com-retorno'`, o vazio fica sempre no fim, nas duas direções, e
 * conta como vazio também o texto só de espaços, o travessão "—", a data que
 * não começa por `AAAA-MM-DD` e o `NaN`. O terceiro clique na mesma coluna
 * devolve a ordem de chegada.
 */

/** Como os valores de uma coluna se comparam. */
export type TipoDeColuna = 'numero' | 'data' | 'texto';

/** O valor que uma coluna tira de uma linha; `null` é o vazio. */
export type ValorOrdenavel = string | number | null;

/** O que a tabela declara de cada coluna ordenável. */
export interface ColunaOrdenavel<L> {
  /** Regra de comparação. Data é texto ISO `AAAA-MM-DD`. */
  tipo: TipoDeColuna;
  /** Tira o valor da linha. */
  valor: (linha: L) => ValorOrdenavel;
}

/** A direção da coluna ativa. */
export type Direcao = 'crescente' | 'decrescente';

/** Qual coluna ordena a tabela, e em que direção. */
export interface EstadoDeOrdenacao {
  /** A chave da coluna ativa, ou `null` antes do primeiro clique. */
  coluna: string | null;
  direcao: Direcao;
}

/**
 * Como os cliques se sucedem e onde o vazio fica.
 *
 * `'alternada'` alterna entre crescente e decrescente, com o vazio no começo da
 * crescente; `'com-retorno'` passa por crescente, decrescente e de volta à
 * ordem de chegada, com o vazio sempre no fim.
 */
export type RegraDeOrdenacao = 'alternada' | 'com-retorno';

/** O estado de uma tabela que ninguém ordenou: a ordem é a de chegada. */
export const SEM_ORDENACAO: EstadoDeOrdenacao = { coluna: null, direcao: 'crescente' };

const COLADOR = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

/**
 * O estado depois de um clique num cabeçalho.
 *
 * Clicar em outra coluna começa crescente nela, nas duas regras. Na
 * `'alternada'`, clicar na coluna ativa inverte a direção, e não existe estado
 * que volte à ordem de chegada: só recarregar a página desfaz a ordenação. Na
 * `'com-retorno'`, o clique na coluna ativa decrescente devolve
 * `SEM_ORDENACAO`.
 *
 * @param atual - O estado antes do clique.
 * @param coluna - A chave da coluna clicada.
 * @param regra - A regra da tabela; sem ela, `'alternada'`.
 * @returns O estado novo.
 */
export function proximoEstado(
  atual: EstadoDeOrdenacao,
  coluna: string,
  regra: RegraDeOrdenacao = 'alternada',
): EstadoDeOrdenacao {
  if (atual.coluna === coluna) {
    if (atual.direcao === 'crescente') return { coluna, direcao: 'decrescente' };
    return regra === 'com-retorno' ? SEM_ORDENACAO : { coluna, direcao: 'crescente' };
  }
  return { coluna, direcao: 'crescente' };
}

/**
 * Converte um valor de coluna numérica no número que a origem compararia.
 *
 * @param valor - O valor tirado da linha.
 * @returns O próprio número, ou `-Infinity` para vazio, texto e `NaN`.
 */
function comoNumero(valor: ValorOrdenavel): number {
  return typeof valor === 'number' && !Number.isNaN(valor) ? valor : Number.NEGATIVE_INFINITY;
}

/**
 * Compara dois valores de uma coluna.
 *
 * Quem chama é `ordenarLinhas`; fica exportada para os testes e para a tabela
 * que precise comparar fora de uma ordenação. Data e texto seguem a mesma regra
 * — o `localeCompare` do português com números naturais —, que ordena certo o
 * texto ISO porque cada parte da data tem largura fixa.
 *
 * @param a - Valor da primeira linha.
 * @param b - Valor da segunda linha.
 * @param tipo - O tipo declarado da coluna.
 * @returns Negativo se `a` vem antes, positivo se depois, zero se empatam.
 */
export function compararValores(
  a: ValorOrdenavel,
  b: ValorOrdenavel,
  tipo: TipoDeColuna,
): number {
  if (tipo === 'numero') {
    const x = comoNumero(a);
    const y = comoNumero(b);
    if (x === y) return 0;
    return x < y ? -1 : 1;
  }
  return COLADOR.compare(a === null ? '' : String(a), b === null ? '' : String(b));
}

/**
 * Reduz um valor ao que a regra `'com-retorno'` compara, ou a `null` se é vazio.
 *
 * @param valor - O valor tirado da linha.
 * @param tipo - O tipo declarado da coluna.
 * @returns O número, o texto aparado ou a data ISO; `null` para o vazio.
 */
function semVazio(valor: ValorOrdenavel, tipo: TipoDeColuna): ValorOrdenavel {
  if (valor === null) return null;
  if (tipo === 'numero') {
    return typeof valor === 'number' && !Number.isNaN(valor) ? valor : null;
  }
  const texto = String(valor).trim();
  if (tipo === 'data') return /^\d{4}-\d{2}-\d{2}/.test(texto) ? texto : null;
  return texto === '' || texto === '—' ? null : texto;
}

/**
 * Compara dois valores pondo o vazio no fim, qualquer que seja a direção.
 *
 * @param a - Valor da primeira linha.
 * @param b - Valor da segunda linha.
 * @param tipo - O tipo declarado da coluna.
 * @param sinal - `1` na crescente, `-1` na decrescente.
 * @returns Negativo se `a` vem antes, positivo se depois, zero se empatam.
 */
function compararComVazioNoFim(
  a: ValorOrdenavel,
  b: ValorOrdenavel,
  tipo: TipoDeColuna,
  sinal: number,
): number {
  const x = semVazio(a, tipo);
  const y = semVazio(b, tipo);
  if (x === null && y === null) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  return compararValores(x, y, tipo) * sinal;
}

/**
 * Ordena as linhas pela coluna ativa.
 *
 * Devolve sempre um array novo, mesmo sem ordenação, e nunca altera o recebido.
 * O empate fica na ordem de chegada, nas duas direções, porque o `sort` do
 * JavaScript é estável e a decrescente só troca o sinal da comparação. Na
 * regra `'com-retorno'` o sinal não alcança o vazio, que fica no fim também na
 * decrescente.
 *
 * @param linhas - As linhas na ordem de chegada.
 * @param colunas - A declaração das colunas ordenáveis, por chave.
 * @param estado - Qual coluna e qual direção.
 * @param regra - A regra da tabela; sem ela, `'alternada'`.
 * @returns As linhas ordenadas; na ordem de chegada se a coluna não for
 *   declarada.
 */
export function ordenarLinhas<L>(
  linhas: readonly L[],
  colunas: Readonly<Record<string, ColunaOrdenavel<L>>>,
  estado: EstadoDeOrdenacao,
  regra: RegraDeOrdenacao = 'alternada',
): L[] {
  const declarada = estado.coluna === null ? undefined : colunas[estado.coluna];
  if (declarada === undefined) return [...linhas];
  const sinal = estado.direcao === 'crescente' ? 1 : -1;
  if (regra === 'com-retorno') {
    return [...linhas].sort((a, b) =>
      compararComVazioNoFim(declarada.valor(a), declarada.valor(b), declarada.tipo, sinal),
    );
  }
  return [...linhas].sort(
    (a, b) => compararValores(declarada.valor(a), declarada.valor(b), declarada.tipo) * sinal,
  );
}
